import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AuthShell from '@/components/layout/AuthShell';
import { BrandLockup } from '@/components/Brand';
import ThemeToggle from '@/components/ThemeToggle';
import {
  AuthField,
  AuthInput,
  PasswordInput,
  AuthSubmit,
  FormAlert,
  formatApiError,
} from '@/components/layout/AuthSplit';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Staff sign-in. Deliberately plain: no marketing panel, no sample data,
 * no platform chrome and no username hint. The amber accent (admin panel
 * variant) is the only thing that sets it apart from the customer login.
 */
const AdminLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [error, setError] = useState('');
  const [badCredentials, setBadCredentials] = useState(false);

  useEffect(() => {
    if (localStorage.getItem('admin_token')) navigate('/admin/dashboard', { replace: true });
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setError('');
    setBadCredentials(false);
    try {
      const response = await axios.post(`${API}/admin/login`, {
        username: formData.username.trim(),
        password: formData.password,
      });
      localStorage.setItem('admin_token', response.data.token);
      toast.success('Signed in to the admin console');
      navigate('/admin/dashboard');
    } catch (err) {
      if (err.response?.status === 401) {
        setBadCredentials(true);
        setError('The username or password is incorrect.');
      } else {
        setError(formatApiError(err, 'Sign-in failed. Try again.').message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell variant="card" panelVariant="admin">
      <div className="auth-card-page">
        <div className="auth-card-page-top">
          <Link to="/" className="auth-back">Back to site</Link>
          <div style={{ flex: 1 }} />
          <ThemeToggle />
        </div>

        <div className="auth-card-page-body">
          <div className="auth-card">
            <div style={{ marginBottom: 24 }}>
              <BrandLockup size={26} />
            </div>
            <div className="auth-head" style={{ marginBottom: 20 }}>
              <div className="auth-eyebrow">Admin console</div>
              <h1 className="auth-title" style={{ fontSize: 'clamp(28px, 3vw, 34px)' }}>
                Staff <em>sign-in.</em>
              </h1>
              <p className="auth-intro">For IntelliSecure operators only. Customer accounts sign in on the main sign-in page.</p>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-notice">
                <ClipboardList aria-hidden="true" />
                Sign-ins and console actions are recorded in the audit log.
              </div>

              <FormAlert id="admin-login-error" message={error} />

              <AuthField id="admin-username" label="Admin username">
                <AuthInput
                  id="admin-username"
                  name="username"
                  data-testid="admin-username-input"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="off"
                  spellCheck={false}
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  invalid={badCredentials}
                  aria-describedby={badCredentials ? 'admin-login-error' : undefined}
                  required
                />
              </AuthField>

              <AuthField id="admin-password" label="Password">
                <PasswordInput
                  id="admin-password"
                  name="password"
                  data-testid="admin-password-input"
                  autoComplete="current-password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  invalid={badCredentials}
                  aria-describedby={badCredentials ? 'admin-login-error' : undefined}
                  required
                />
              </AuthField>

              <AuthSubmit data-testid="admin-login-submit-btn" loading={loading} loadingLabel="Signing in…">
                Sign in to console
                <ArrowRight aria-hidden="true" />
              </AuthSubmit>
            </form>
          </div>
        </div>
      </div>
    </AuthShell>
  );
};

export default AdminLogin;
